import React, { useState, useCallback, useMemo, useContext } from 'react';

import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { Checkbox } from 'ui-components/components/checkbox';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { RadioCheckList } from 'ui-components/components/radio-check-list';
import { RadioCheckListItem } from 'ui-components/components/radio-check-list/radio-check-list-item';
import { WeightInput } from 'ui-components/components/weight-input';
import { emptyObject } from 'ui-components/default-props/empty';
import { DimensionInput } from 'ui-components/components/dimension-input';

import { AddressFactory } from '../../../../../../address';
import { EMAIL, PHONE, NAME, PASTE_ADDRESS, COMPANY, LINE1, LINE2, CITY, STATE } from '../../../../../../address/constants';
import { addressFields } from '../../../../../../components/billing-factory/constants';
import { handleFormFieldValidation } from '../../../../../../components/billing-factory/form-validation';
import Global from '../../../../../../store/global-selectors';
import { useFeatureFlag } from '../../../../../../feature-flags/hooks/use-feature-flag';
import { FeatureFlagDeclarations } from '../../../../../../feature-flags/feature-flag-declarations';

import messages from '../../messages';
import { RESIDENTIAL, COMMERCIAL, SKIP_DIMENSIONS } from '../constants';
import { DeliveryOptionsContext } from '../context';
import DeliveryOptionsApiService from '../delivery-options-api-service';
import style from './preview-modal.module.less';
import { RateRow } from './rate-row';
import { RateRowPlaceholder } from './rate-row-placeholder';

const PreviewModal = () => {
  const { marketplaceNameObject, storeGuid, items: deliveryOptions, calculationMethod } = useContext(DeliveryOptionsContext);

  const isLiveRatesWithDimensionsEnabled = calculationMethod !== SKIP_DIMENSIONS;
  // TODO SPD-15175: Create unique feature flag for this
  const shouldRequireFullAddress = useFeatureFlag(FeatureFlagDeclarations.enableLiveRatesWithDimensions);
  const homeCountry = useSelector(Global.selectSellerHomeCountry);
  const sellerDefaultWeightUnit = useSelector(Global.selectSellerDefaultWeightUnit);
  const sellerDefaultDimensionUnit = useSelector(Global.selectSellerDefaultDimensionUnit);

  const [loading, setLoading] = useState(false);
  const [isAdvancedModeActive, setIsAdvancedModeActive] = useState(shouldRequireFullAddress);
  const [rates, setRates] = useState([]);
  const [errorObj, setErrorObj] = useState(emptyObject);
  const [weight, setWeight] = useState(0);
  const [dimensions, setDimensions] = useState({
    length: 0,
    width: 0,
    height: 0,
  });
  const [residentialIndicator, setResidentialIndicator] = useState(RESIDENTIAL);

  const defaultAddress = {
    line1: '',
    line2: '',
    city: '',
    state: '',
    postalCode: '',
    countryCode: homeCountry,
  };
  const [addressInfo, setAddressInfo] = useState(defaultAddress);
  const { line1, line2, city, postalCode, countryCode } = addressInfo;

  const changeMode = useCallback(() => {
    setAddressInfo({
      ...addressInfo,
      line1: '',
      line2: '',
      city: '',
      state: '',
    });
    setErrorObj(emptyObject);
    setIsAdvancedModeActive((prevValue) => !prevValue);
  }, [addressInfo]);
  const onAddressChange = useCallback(
    (fieldName, value) => {
      setAddressInfo({
        ...addressInfo,
        [fieldName]: value,
      });
    },
    [addressInfo],
  );
  const previewShippingCostsCallback = useCallback(() => {
    setRates([]);
    setLoading(true);
    DeliveryOptionsApiService.getPreviewData({
      ...addressInfo,
      street1: line1,
      street2: line2,
      zipCode: postalCode,
      residentialIndicator,
      storeGuid,
      weight: {
        value: weight,
        unit: sellerDefaultWeightUnit,
      },
      dimensions: {
        ...dimensions,
        unit: sellerDefaultDimensionUnit,
      },
    }).then((newRates) => {
      setRates(newRates);
      setLoading(false);
    });
  }, [
    addressInfo,
    line1,
    line2,
    postalCode,
    residentialIndicator,
    storeGuid,
    weight,
    sellerDefaultWeightUnit,
    dimensions,
    sellerDefaultDimensionUnit,
  ]);

  const includedRates = useMemo(() => rates.filter((rate) => rate.isIncluded), [rates]);
  const excludedRates = useMemo(() => rates.filter((rate) => !rate.isIncluded), [rates]);

  const rateContent = (
    <>
      {includedRates.map((rate) => (
        <RateRow key={rate.optionId} firstActiveRateId={includedRates[0]?.optionId} rate={rate} />
      ))}
      {excludedRates.map((rate) => (
        <RateRow key={rate.optionId} rate={rate} />
      ))}
    </>
  );

  const hiddenFields = isAdvancedModeActive
    ? [EMAIL, PHONE, NAME, PASTE_ADDRESS, COMPANY]
    : [EMAIL, PHONE, NAME, PASTE_ADDRESS, COMPANY, LINE1, LINE2, CITY, STATE];
  const validateFormValues = useCallback(
    (fieldName, value) => {
      if (addressFields[fieldName] === fieldName) {
        const error = handleFormFieldValidation(fieldName, value);
        setErrorObj({ ...errorObj, [fieldName]: error });
      }
    },
    [errorObj],
  );
  const formHasErrors = useMemo(() => Object.keys(errorObj).filter((key) => errorObj[key] === true).length > 0, [errorObj]);

  const minimumRequiredFields = isLiveRatesWithDimensionsEnabled
    ? [postalCode, countryCode, weight, dimensions.length, dimensions.width, dimensions.height]
    : [postalCode, countryCode, weight];
  const minimumRequiredFieldsAdvancedMode = [line1, city, ...minimumRequiredFields];
  const requiredFields = isAdvancedModeActive ? minimumRequiredFieldsAdvancedMode : minimumRequiredFields;
  const formIsIncomplete = requiredFields.some((v) => !v);
  const disableSubmit = formHasErrors || formIsIncomplete;
  const marketPlaceDisplayName = marketplaceNameObject.values?.name || marketplaceNameObject.defaultMessage;

  return (
    <Modal className={style['preview-modal']}>
      <ModalHeader>
        <FormattedMessage {...messages.previewModalTitleCR} values={{ marketplaceName: marketPlaceDisplayName }} />
      </ModalHeader>
      <ModalBody>
        <div className={style['preview-modal']}>
          <div className={style['preview-form']}>
            <div className={style['preview-form-top']}>
              <div className={style['preview-form-title']}>
                <FormattedMessage {...messages.shippingDetails} />
              </div>
              {!shouldRequireFullAddress && !isAdvancedModeActive && (
                <div className={style['required-message']}>
                  <FormattedMessage {...messages.advancedModeExplainer} />
                </div>
              )}
              <div className={style['preview-form-item-title']}>
                <FormattedMessage {...messages.addressDesignation} />
              </div>
              <RadioCheckList className={style['address-type-radio-wrapper']}>
                <RadioCheckListItem isChecked={residentialIndicator === RESIDENTIAL} onChecked={() => setResidentialIndicator(RESIDENTIAL)}>
                  <FormattedMessage {...messages.residential} />
                </RadioCheckListItem>
                <RadioCheckListItem isChecked={residentialIndicator === COMMERCIAL} onChecked={() => setResidentialIndicator(COMMERCIAL)}>
                  <FormattedMessage {...messages.commercial} />
                </RadioCheckListItem>
              </RadioCheckList>
              <AddressFactory
                address={addressInfo}
                autoCompleteRecipientName={false}
                hasError={errorObj}
                hiddenFields={hiddenFields}
                isControlled
                isEdit
                onBlur={validateFormValues}
                onChange={onAddressChange}
                showAddressLine3={false}
                wrapperClassName={style['address-wrapper']}
              />
              <hr className={style['weight-divider']} />
              <div className={style['preview-form-item-title']}>
                <FormattedMessage {...messages.packageWeight} />
              </div>
              <div>
                <WeightInput className={style['input-item']} onChange={setWeight} unit={sellerDefaultWeightUnit} weight={weight} />
              </div>
              {isLiveRatesWithDimensionsEnabled && (
                <>
                  <div className={style['preview-form-item-title']}>
                    <FormattedMessage {...messages.packageDimensions} />
                  </div>
                  <div>
                    <DimensionInput
                      dimensions={dimensions}
                      inputClassName={style['dimension-input']}
                      onChange={setDimensions}
                      unit={sellerDefaultDimensionUnit}
                    />
                  </div>
                </>
              )}
            </div>
            <div className={style['preview-footer']}>
              <div>
                {!shouldRequireFullAddress && (
                  <Checkbox
                    checked={isAdvancedModeActive}
                    label={<FormattedMessage {...messages.showAdvancedPreview} />}
                    onChange={changeMode}
                  />
                )}
              </div>
              <Button
                data-pendo-id='/settings/stores#preview-delivery-option-displays'
                disabled={disableSubmit}
                onClick={previewShippingCostsCallback}
                theme='primary'
              >
                <FormattedMessage {...messages.previewRates} />
              </Button>
            </div>
          </div>
          <div className={style['preview-section']}>
            <div className={style['preview-title']}>
              <FormattedMessage {...messages.previewDeliveryOption} />
            </div>
            <div className={style['preview-description']}>
              <FormattedMessage {...messages.previewDescription} values={{ marketplaceName: marketPlaceDisplayName }} />
            </div>
            <div className={style['preview-list']}>
              <FormattedMessage {...messages.checkoutRateDisplays} />
            </div>
            <div className={style['preview-content']}>
              {loading && (
                <div className={style['loading-indicator-wrapper']}>
                  <LoadingIndicator className={style['loading-indicator']} />
                </div>
              )}
              {rates.length === 0 && !loading && deliveryOptions.map((option) => <RateRowPlaceholder key={option.id} option={option} />)}
              {rates.length > 0 && !loading && rateContent}
            </div>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default PreviewModal;
